import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Badge, Button } from '@/components/atoms';
import { editoriasApi } from '@/services/editorias';
import {
  CHAVE_ATIVO,
  CHAVE_PERCENTUAL,
  ENDPOINTS,
  SCOPES,
  wireseoConfigApi,
  type ParamRow,
  type ParamScope,
  type RegistryKind,
  type RegistryRow,
} from '@/services/wireseoConfig';

import { PainelDeErro } from './Aguardando';
import styles from './WireseoConfig.module.css';

/**
 * Superfícies de configuração do WIRESEO (plano §10), dentro de /engineering —
 * a rota já é ADMIN-ONLY, então nada aqui afrouxa permissão.
 *
 * As rotas /wireseo/* não aceitam `client`: o dono da linha é o principal do
 * token. Quem é staff grava a linha padrão da CASA, herdada por todos os
 * tenants. O aviso no topo de cada bloco diz isso — esconder essa diferença
 * seria repetir o bug que fez a casa editar o prompt de um cliente e mudar a
 * saída de outro.
 */

const AVISO_DONO =
  'Estas linhas pertencem ao principal do seu token, não ao cliente escolhido no topo: se você é da casa, o que gravar aqui vira o padrão herdado por todos os clientes.';

function comoTexto(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

/** Texto do campo vira JSON quando é JSON; senão vale como string literal. */
function comoValor(txt: string): unknown {
  const t = txt.trim();
  if (t === '') return '';
  try {
    return JSON.parse(t);
  } catch {
    return txt;
  }
}

/* ══════════════════════════════════════════════════════════════════════════
   1 · CASCATA DE PARÂMETROS  —  GET/PUT /wireseo/params
   ══════════════════════════════════════════════════════════════════════════ */

export function WireseoParams() {
  const qc = useQueryClient();
  const [rascunho, setRascunho] = useState<Record<string, string>>({});
  const [novo, setNovo] = useState<{
    scope: ParamScope;
    ref: string;
    chave: string;
    valor: string;
  }>({ scope: 'plataforma', ref: '', chave: '', valor: '' });

  const q = useQuery({
    queryKey: ['wireseo-params'],
    queryFn: wireseoConfigApi.getParams,
    retry: false,
  });

  const salvar = useMutation({
    mutationFn: wireseoConfigApi.putParams,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wireseo-params'] }),
  });

  if (q.isError) {
    return (
      <PainelDeErro
        endpoint={`GET ${ENDPOINTS.params}`}
        o_que="A cascata de parâmetros (plataforma, esteira, editoria, matéria)"
        erro={q.error}
      />
    );
  }

  const rows = q.data?.rows ?? [];
  const defaults = q.data?.defaults ?? {};
  const efetivo = q.data?.efetivo ?? {};
  const idDe = (p: ParamRow) => `${p.scope}::${p.scope_ref}::${p.chave}`;

  return (
    <div className={styles.bloco}>
      <p className={styles.explica}>
        Cada camada sobrescreve a anterior: plataforma, depois esteira, depois editoria e, por
        último, a matéria. {AVISO_DONO}
      </p>

      {q.isLoading && <p className={styles.vazio}>Carregando parâmetros…</p>}

      {/* ── o EFETIVO: o que o motor vai usar de verdade ── */}
      {Object.keys(efetivo).length > 0 && (
        <section className={styles.scope}>
          <header className={styles.scopeHead}>
            <span className={styles.scopeNome}>Valor efetivo</span>
            <Badge tone="info" size="xs">
              o que o motor usa
            </Badge>
          </header>
          <p className={styles.scopeExp}>
            Resultado da cascata já resolvida. &ldquo;de fábrica&rdquo; é o valor que vem do código;
            &ldquo;gravado&rdquo; é o que alguma camada mudou.
          </p>
          <table className={styles.tabela}>
            <thead>
              <tr>
                <th>Chave</th>
                <th>Valor efetivo</th>
                <th>Origem</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(efetivo).map(([k, v]) => {
                const igualDefault = k in defaults && comoTexto(defaults[k]) === comoTexto(v);
                return (
                  <tr key={k}>
                    <td className={styles.mono}>{k}</td>
                    <td className={styles.mono}>{comoTexto(v) || '(vazio)'}</td>
                    <td>
                      <Badge tone={igualDefault ? 'neutral' : 'warn'} size="xs">
                        {igualDefault ? 'de fábrica' : 'gravado'}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {/* ── as linhas gravadas, por camada ── */}
      {SCOPES.map((sc) => {
        const doScope = rows.filter((p) => p.scope === sc.key);
        return (
          <section key={sc.key} className={styles.scope}>
            <header className={styles.scopeHead}>
              <span className={styles.scopeNome}>{sc.rotulo}</span>
              <Badge tone="neutral" size="xs">
                {doScope.length} linhas gravadas
              </Badge>
            </header>
            <p className={styles.scopeExp}>{sc.explicacao}</p>
            {doScope.length === 0 && !q.isLoading && (
              <p className={styles.vazio}>
                Nada gravado nesta camada — vale o valor de fábrica da tabela acima.
              </p>
            )}
            {doScope.length > 0 && (
              <table className={styles.tabela}>
                <thead>
                  <tr>
                    <th>Alvo</th>
                    <th>Chave</th>
                    <th>Valor</th>
                    <th>Alterado por</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {doScope.map((p) => {
                    const k = idDe(p);
                    const atual = rascunho[k] ?? comoTexto(p.valor);
                    const sujo = atual !== comoTexto(p.valor);
                    return (
                      <tr key={k}>
                        <td className={styles.mono}>{p.scope_ref || '(toda a plataforma)'}</td>
                        <td className={styles.mono}>{p.chave}</td>
                        <td>
                          <input
                            className={styles.input}
                            value={atual}
                            onChange={(e) => setRascunho((r) => ({ ...r, [k]: e.target.value }))}
                            aria-label={`Valor de ${p.chave}`}
                          />
                        </td>
                        <td className={styles.subtexto}>{p.updated_by ?? 'sem registro'}</td>
                        <td className={styles.acoesCel}>
                          <Button
                            variant="secondary"
                            size="sm"
                            disabled={!sujo || salvar.isPending}
                            onClick={() =>
                              salvar.mutate({
                                scope: p.scope,
                                scope_ref: p.scope_ref,
                                valores: { [p.chave]: comoValor(atual) },
                              })
                            }
                          >
                            Gravar
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={salvar.isPending}
                            title="Apaga a linha e devolve a chave ao valor de fábrica"
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Apagar o parâmetro "${p.chave}" desta camada? A chave volta ao valor de fábrica.`,
                                )
                              )
                                salvar.mutate({
                                  scope: p.scope,
                                  scope_ref: p.scope_ref,
                                  valores: { [p.chave]: null },
                                });
                            }}
                          >
                            Apagar
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
        );
      })}

      <section className={styles.scope}>
        <header className={styles.scopeHead}>
          <span className={styles.scopeNome}>Novo parâmetro</span>
        </header>
        <div className={styles.formLinha}>
          <label className={styles.campo}>
            <span>Camada</span>
            <select
              value={novo.scope}
              onChange={(e) => setNovo((n) => ({ ...n, scope: e.target.value as ParamScope }))}
            >
              {SCOPES.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.rotulo}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.campo}>
            <span>Alvo (esteira, editoria ou matéria)</span>
            <input
              value={novo.ref}
              onChange={(e) => setNovo((n) => ({ ...n, ref: e.target.value }))}
              placeholder="vazio = toda a plataforma"
              disabled={novo.scope === 'plataforma'}
            />
          </label>
          <label className={styles.campo}>
            <span>Chave</span>
            <input
              value={novo.chave}
              onChange={(e) => setNovo((n) => ({ ...n, chave: e.target.value }))}
              placeholder="ex.: max_links"
              list="wireseo-chaves"
            />
            <datalist id="wireseo-chaves">
              {Object.keys(defaults).map((k) => (
                <option key={k} value={k} />
              ))}
            </datalist>
          </label>
          <label className={styles.campo}>
            <span>Valor</span>
            <input
              value={novo.valor}
              onChange={(e) => setNovo((n) => ({ ...n, valor: e.target.value }))}
              placeholder="texto ou JSON"
            />
          </label>
          <Button
            variant="primary"
            disabled={!novo.chave.trim() || salvar.isPending}
            onClick={() =>
              salvar.mutate({
                scope: novo.scope,
                scope_ref: novo.scope === 'plataforma' ? '' : novo.ref.trim(),
                valores: { [novo.chave.trim()]: comoValor(novo.valor) },
              })
            }
          >
            {salvar.isPending ? 'A gravar…' : 'Gravar parâmetro'}
          </Button>
        </div>
        {salvar.isError && (
          <p className={styles.erro}>
            Falha ao gravar: {(salvar.error as Error)?.message || String(salvar.error)}
          </p>
        )}
      </section>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   2 · REGISTROS TIPO / TOM  —  GET/PUT /wireseo/registry
   ══════════════════════════════════════════════════════════════════════════ */

const KINDS: { key: RegistryKind; rotulo: string; explicacao: string }[] = [
  {
    key: 'tipo',
    rotulo: 'Tipos de manchete',
    explicacao:
      'Arquétipos que o estrategista pode escolher. A instrução entra no prompt como está escrita.',
  },
  {
    key: 'tom',
    rotulo: 'Tons',
    explicacao: 'A descrição do tom é o texto que o executor recebe para calibrar a redação.',
  },
];

export function WireseoRegistry() {
  const qc = useQueryClient();
  const [rascunho, setRascunho] = useState<Record<string, Partial<RegistryRow>>>({});

  const q = useQuery({
    queryKey: ['wireseo-registry'],
    queryFn: wireseoConfigApi.getRegistry,
    retry: false,
  });

  const salvar = useMutation({
    mutationFn: wireseoConfigApi.putRegistry,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wireseo-registry'] }),
  });

  if (q.isError) {
    return (
      <PainelDeErro
        endpoint={`GET ${ENDPOINTS.registry}`}
        o_que="Os registros de TIPO de manchete e de TOM"
        erro={q.error}
      />
    );
  }

  const rows = q.data?.rows ?? [];
  const efetivo = q.data?.efetivo;

  return (
    <div className={styles.bloco}>
      <p className={styles.explica}>{AVISO_DONO}</p>
      {q.isLoading && <p className={styles.vazio}>Carregando registros…</p>}

      {KINDS.map((k) => {
        const doKind = rows.filter((i) => i.kind === k.key);
        const plural = k.key === 'tipo' ? 'tipos' : 'tons';
        const habilitados = (efetivo?.[`${plural}_habilitados`] as string[] | undefined) ?? [];
        const permitidos = (efetivo?.[`${plural}_permitidos`] as string[] | undefined) ?? [];
        return (
          <section key={k.key} className={styles.scope}>
            <header className={styles.scopeHead}>
              <span className={styles.scopeNome}>{k.rotulo}</span>
              <Badge tone="neutral" size="xs">
                {doKind.length} registros
              </Badge>
              {efetivo && (
                <Badge tone="info" size="xs">
                  {habilitados.length} habilitados · {permitidos.length} permitidos
                </Badge>
              )}
            </header>
            <p className={styles.scopeExp}>{k.explicacao}</p>
            {doKind.length === 0 && !q.isLoading && (
              <p className={styles.vazio}>
                Nenhum registro gravado nesta instalação. Sem registro, o motor não tem arquétipo
                para escolher.
              </p>
            )}
            {doKind.map((it) => {
              const chave = `${it.kind}::${it.slug}`;
              const d = rascunho[chave] ?? {};
              const merged: RegistryRow = { ...it, ...d };
              const sujo = Object.keys(d).length > 0;
              return (
                <div key={chave} className={styles.registro}>
                  <div className={styles.registroTopo}>
                    <span className={styles.mono}>{it.slug}</span>
                    {it.client_id === null && (
                      <Badge tone="neutral" size="xs">
                        padrão da casa
                      </Badge>
                    )}
                    <input
                      className={styles.input}
                      value={merged.rotulo ?? ''}
                      onChange={(e) =>
                        setRascunho((r) => ({ ...r, [chave]: { ...d, rotulo: e.target.value } }))
                      }
                      aria-label={`Rótulo de ${it.slug}`}
                    />
                    <label className={styles.check}>
                      <input
                        type="checkbox"
                        checked={merged.habilitado}
                        onChange={(e) =>
                          setRascunho((r) => ({
                            ...r,
                            [chave]: { ...d, habilitado: e.target.checked },
                          }))
                        }
                      />
                      <span>habilitado</span>
                    </label>
                    <label className={styles.check}>
                      <input
                        type="checkbox"
                        checked={merged.exige_liberacao_chefe}
                        onChange={(e) =>
                          setRascunho((r) => ({
                            ...r,
                            [chave]: { ...d, exige_liberacao_chefe: e.target.checked },
                          }))
                        }
                      />
                      <span>exige liberação do chefe</span>
                    </label>
                    <span className={styles.spacer} />
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={!sujo || salvar.isPending}
                      onClick={() => {
                        salvar.mutate({
                          kind: merged.kind,
                          slug: merged.slug,
                          rotulo: merged.rotulo,
                          instrucao: merged.instrucao,
                          habilitado: merged.habilitado,
                          exige_liberacao_chefe: merged.exige_liberacao_chefe,
                        });
                        setRascunho((r) =>
                          Object.fromEntries(Object.entries(r).filter(([kk]) => kk !== chave)),
                        );
                      }}
                    >
                      Gravar
                    </Button>
                  </div>
                  <textarea
                    className={styles.textarea}
                    value={merged.instrucao ?? ''}
                    onChange={(e) =>
                      setRascunho((r) => ({ ...r, [chave]: { ...d, instrucao: e.target.value } }))
                    }
                    aria-label={`Instrução de ${it.slug}`}
                  />
                </div>
              );
            })}
          </section>
        );
      })}
      {salvar.isError && (
        <p className={styles.erro}>
          Falha ao gravar: {(salvar.error as Error)?.message || String(salvar.error)}
        </p>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   3 · FONTES OFICIAIS  —  GET/PUT /wireseo/fontes-oficiais
   ══════════════════════════════════════════════════════════════════════════ */

export function WireseoFontes() {
  const qc = useQueryClient();
  const [nova, setNova] = useState({ orgao: '', dominio: '' });

  const q = useQuery({
    queryKey: ['wireseo-fontes'],
    queryFn: wireseoConfigApi.getFontes,
    retry: false,
  });

  const salvar = useMutation({
    mutationFn: wireseoConfigApi.putFonte,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wireseo-fontes'] }),
  });

  // Lista plana de domínios ATIVOS. Já foi um mapa órgão→domínios; indexar a
  // lista pelo órgão dava `undefined` calado e a coluna respondia "não" para
  // todas as fontes ativas. Conjunto para a busca não virar O(n²) na tabela.
  // Fica ANTES do retorno de erro: hook depois de early return muda a ordem
  // dos hooks entre renders.
  const allowlist = useMemo(() => new Set(q.data?.allowlist ?? []), [q.data]);

  if (q.isError) {
    return (
      <PainelDeErro
        endpoint={`GET ${ENDPOINTS.fontes}`}
        o_que="O dicionário de fontes oficiais (órgão para domínio)"
        erro={q.error}
      />
    );
  }

  const rows = q.data?.rows ?? [];

  return (
    <div className={styles.bloco}>
      <p className={styles.explica}>
        Este dicionário é a allowlist da fonte primária: o motor só ancora link externo em domínio
        que esteja aqui e responda. Quando uma matéria devolve fato sem fonte, o conserto é
        acrescentar o órgão nesta lista — a próxima acha sozinha. {AVISO_DONO}
      </p>
      {q.isLoading && <p className={styles.vazio}>Carregando fontes…</p>}
      {rows.length === 0 && !q.isLoading && (
        <p className={styles.vazio}>Nenhuma fonte gravada nesta instalação.</p>
      )}
      {rows.length > 0 && (
        <table className={styles.tabela}>
          <thead>
            <tr>
              <th>Órgão</th>
              <th>Domínio</th>
              <th>Na allowlist</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((f) => (
              <tr key={f.id}>
                <td>{f.orgao}</td>
                <td className={styles.mono}>{f.dominio}</td>
                <td>
                  <Badge tone={allowlist.has(f.dominio) ? 'ok' : 'neutral'} size="xs">
                    {allowlist.has(f.dominio) ? 'sim' : 'não'}
                  </Badge>
                </td>
                <td>
                  <label className={styles.check}>
                    <input
                      type="checkbox"
                      checked={f.ativo}
                      disabled={salvar.isPending}
                      onChange={(e) =>
                        salvar.mutate({
                          orgao: f.orgao,
                          dominio: f.dominio,
                          ativo: e.target.checked,
                        })
                      }
                    />
                    <span>{f.ativo ? 'em uso' : 'desligada'}</span>
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className={styles.formLinha}>
        <label className={styles.campo}>
          <span>Órgão</span>
          <input
            value={nova.orgao}
            onChange={(e) => setNova((n) => ({ ...n, orgao: e.target.value }))}
            placeholder="ex.: STF"
          />
        </label>
        <label className={styles.campo}>
          <span>Domínio</span>
          <input
            value={nova.dominio}
            onChange={(e) => setNova((n) => ({ ...n, dominio: e.target.value }))}
            placeholder="ex.: portal.stf.jus.br"
          />
        </label>
        <Button
          variant="primary"
          disabled={!nova.orgao.trim() || !nova.dominio.trim() || salvar.isPending}
          onClick={() =>
            salvar.mutate({
              orgao: nova.orgao.trim(),
              dominio: nova.dominio.trim(),
              ativo: true,
            })
          }
        >
          {salvar.isPending ? 'A gravar…' : 'Acrescentar fonte'}
        </Button>
      </div>
      {salvar.isError && (
        <p className={styles.erro}>
          Falha ao gravar: {(salvar.error as Error)?.message || String(salvar.error)}
        </p>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   4 · LOSANGO POR ESTEIRA  —  PUT /wireseo/params com scope=esteira
   ══════════════════════════════════════════════════════════════════════════ */

export function WireseoEsteiras() {
  const qc = useQueryClient();
  const [pct, setPct] = useState<Record<string, string>>({});

  const esteiras = useQuery({
    queryKey: ['editorias'],
    queryFn: editoriasApi.list,
    retry: false,
  });

  const params = useQuery({
    queryKey: ['wireseo-params'],
    queryFn: wireseoConfigApi.getParams,
    retry: false,
  });

  const salvar = useMutation({
    mutationFn: wireseoConfigApi.putParams,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wireseo-params'] }),
  });

  const porEsteira = useMemo(() => {
    const m = new Map<string, { ativo: boolean; percentual: number | null }>();
    for (const p of params.data?.rows ?? []) {
      if (p.scope !== 'esteira' || !p.scope_ref) continue;
      const cur = m.get(p.scope_ref) ?? { ativo: false, percentual: null };
      if (p.chave === CHAVE_ATIVO) cur.ativo = p.valor === true || p.valor === 'true';
      if (p.chave === CHAVE_PERCENTUAL) cur.percentual = Number(p.valor);
      m.set(p.scope_ref, cur);
    }
    return m;
  }, [params.data]);

  if (params.isError) {
    return (
      <PainelDeErro
        endpoint={`GET ${ENDPOINTS.params}`}
        o_que="O losango MELHORAR SEO por esteira (liga/desliga e percentual do teste A/B)"
        erro={params.error}
      />
    );
  }

  return (
    <div className={styles.bloco}>
      <p className={styles.explica}>
        <strong>(edição WIRESEO em simulação — ainda não roda em produção.)</strong> A chave e o
        percentual ficam gravados por esteira, mas nenhum motor de publicação os consulta hoje:
        ligar aqui não muda a matéria que vai ao ar. O desenho é o teste lado a lado — com 50,
        metade das matérias passaria pela edição do WIRESEO e metade seguiria como hoje, e o laudo
        marcaria qual foi qual. {AVISO_DONO}
      </p>
      <PainelDeErro endpoint="GET /editorias" o_que="A lista de esteiras" erro={esteiras.error} />
      {(esteiras.isLoading || params.isLoading) && (
        <p className={styles.vazio}>Carregando esteiras…</p>
      )}
      {(esteiras.data ?? []).length > 0 && (
        <table className={styles.tabela}>
          <thead>
            <tr>
              <th>Esteira</th>
              <th>Modo</th>
              <th>
                Melhorar SEO
                <span className={styles.subtexto}>em simulação</span>
              </th>
              <th>Percentual do teste</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(esteiras.data ?? []).map((e) => {
              const cfg = porEsteira.get(e.slug) ?? { ativo: false, percentual: null };
              const gravado = cfg.percentual === null ? '' : String(cfg.percentual);
              const campo = pct[e.slug] ?? gravado;
              const sujo = campo !== gravado;
              return (
                <tr key={e.slug}>
                  <td>
                    {e.nome}
                    <span className={styles.subtexto}>{e.slug}</span>
                  </td>
                  <td className={styles.mono}>{e.modo}</td>
                  <td>
                    <label className={styles.check}>
                      <input
                        type="checkbox"
                        checked={cfg.ativo}
                        disabled={salvar.isPending}
                        onChange={(ev) =>
                          salvar.mutate({
                            scope: 'esteira',
                            scope_ref: e.slug,
                            valores: { [CHAVE_ATIVO]: ev.target.checked },
                          })
                        }
                      />
                      <span>{cfg.ativo ? 'ligado' : 'desligado'}</span>
                    </label>
                  </td>
                  <td>
                    <input
                      className={styles.inputCurto}
                      type="number"
                      min={0}
                      max={100}
                      value={campo}
                      placeholder="sem valor"
                      aria-label={`Percentual do teste na esteira ${e.nome}`}
                      onChange={(ev) => setPct((p) => ({ ...p, [e.slug]: ev.target.value }))}
                    />
                  </td>
                  <td>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={!sujo || salvar.isPending}
                      onClick={() =>
                        salvar.mutate({
                          scope: 'esteira',
                          scope_ref: e.slug,
                          valores: {
                            [CHAVE_PERCENTUAL]: campo === '' ? null : Number(campo),
                          },
                        })
                      }
                    >
                      Gravar
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {salvar.isError && (
        <p className={styles.erro}>
          Falha ao gravar: {(salvar.error as Error)?.message || String(salvar.error)}
        </p>
      )}
    </div>
  );
}
